// services
import AxiosInstanceCreator from 'services/api';

// libraries
import axios from 'axios';

const odmInstance = new AxiosInstanceCreator().create();

export interface OdmProcessingResponse {
  processingId: string;
  status: 'UPLOADING' | 'PENDING' | 'PROCESSING' | 'COMPLETED' | 'FAILED';
}

export interface OdmUploadUrl {
  filename: string;
  url: string;
  contentType: string;
}

export interface OdmUploadUrlResponse {
  processingId: string;
  uploadUrls: OdmUploadUrl[];
}

export interface OdmProcessing {
  id: number;
  processingId: string;
  site: string;
  date: string;
  status: 'UPLOADING' | 'PENDING' | 'PROCESSING' | 'COMPLETED' | 'FAILED';
  imageCount?: number;
  imageUrl?: string;
  selected: boolean;
  del: boolean;
}

export const odmApi = {
  // signed URL 발급: 실제 파일 바이트는 서버를 거치지 않고 이 URL로 GCS에 직접 PUT한다.
  async createUploadSession(site: string, images: File[]): Promise<OdmUploadUrlResponse> {
    const response = await odmInstance.post('/odm/upload-urls', {
      site,
      files: images.map((file) => ({
        filename: file.name,
        contentType: file.type || 'application/octet-stream'
      }))
    });
    return response.data;
  },

  // GCS signed URL로 직접 업로드 — 우리 백엔드 인증 헤더를 붙이면 서명 불일치로 GCS가 거부하므로
  // odmInstance가 아니라 순수 axios로, Content-Type만 (발급받은 값 그대로) 실어 보낸다.
  // signal: 사용자가 취소하면 진행 중인 PUT도 실제로 끊어서 불필요한 업로드가 안 남게 한다.
  async uploadToSignedUrl(
    uploadUrl: OdmUploadUrl,
    file: File,
    signal?: AbortSignal
  ): Promise<void> {
    await axios.put(uploadUrl.url, file, {
      headers: { 'Content-Type': uploadUrl.contentType },
      signal
    });
  },

  // 업로드 중 사용자가 취소(팝업 닫기 등)했을 때 호출 — 즉시 FAILED로 돌려서 reaper 타임아웃을
  // 기다리지 않고 바로 재업로드할 수 있게 한다.
  async cancelUpload(processingId: string): Promise<void> {
    await odmInstance.post(`/odm/${processingId}/upload-cancel`);
  },

  // 백엔드가 매니페스트 대조 실패 시 409로 응답한다 — 이건 예외 상황이 아니라 "거부"라는 정상
  // 응답이므로, axios가 던지지 않게 validateStatus로 받아서 boolean으로 돌려준다.
  async completeUpload(processingId: string): Promise<boolean> {
    const response = await odmInstance.post(`/odm/${processingId}/upload-complete`, undefined, {
      validateStatus: (status) => status === 200 || status === 409
    });
    return response.status === 200;
  },

  async getProcessingStatus(processingId: string): Promise<OdmProcessingResponse> {
    const response = await odmInstance.get(`/odm/status/${processingId}`);
    return response.data;
  },

  async getProcessingList(): Promise<OdmProcessing[]> {
    const response = await odmInstance.get('/odm/list');
    return response.data;
  },

  async getProcessing(id: number): Promise<OdmProcessing> {
    const response = await odmInstance.get(`/odm/${id}`);
    return response.data;
  },

  async selectModel(id: number): Promise<void> {
    await odmInstance.put(`/odm/${id}/select`);
  },

  async deleteProcessing(id: number): Promise<void> {
    await odmInstance.delete(`/odm/${id}`);
  }
};
